import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, MapPin, Calendar, FileText, User, Download, AlertCircle, TrendingUp, Eye, CheckCircle, History } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import adminAuditService from '../../../../../../services/audit_services/adminAuditService';
import attachmentService from '../../../../../../services/audit_services/attachmentService';
import exportQuestionService from '../../../../../../services/audit_services/export_pdf_services/exportQuestionService';

export default function AdminAuditQuestionDetails() {
  const { questionId } = useParams();
  const { authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchQuestionDetails();
  }, [questionId]);

  const fetchQuestionDetails = async () => {
    setLoading(true);
    
    const result = await adminAuditService.getAuditQuestionDetail(authTokens.access, questionId);
    
    if (result.success) {
      setQuestion(result.data);
    } else {
      showError(result.error || 'Erreur lors du chargement des détails de la question');
    }
    
    setLoading(false);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    const result = await exportQuestionService.exportQuestionPDF(authTokens.access, questionId);
    
    if (result.success) {
      showSuccess('Question d\'audit exportée avec succès');
    } else {
      showError(result.error || 'Erreur lors de l\'exportation de la question');
    }
    
    setExporting(false);
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    setDownloadingAttachment(attachmentId);
    
    const result = await attachmentService.downloadAttachment(authTokens.access, attachmentId, fileName);
    
    if (result.success) {
      showSuccess('Téléchargement réussi');
    } else {
      showError(result.error || 'Erreur lors du téléchargement');
    }
    
    setDownloadingAttachment(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En attente' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En cours' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complété' },
      on_hold: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'En pause' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getRiskBadge = (level, type = 'probability') => {
    const riskConfig = {
      critical: { bg: 'bg-red-100', text: 'text-red-800', label: 'Critique' },
      high: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Élevé' },
      medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Moyen' },
      low: { bg: 'bg-green-100', text: 'text-green-800', label: 'Faible' }
    };

    const config = riskConfig[level] || riskConfig.medium;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails de la question...</p>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Question d'audit non trouvée</p>
        <Link to="/dashboard/admin/audits/plans" className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux plans d'audit
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/dashboard/admin/audits/sections/${question.audit_section?.id}/questions`}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{question.question_name}</h1>
            <p className="text-sm text-gray-600 mt-1">Détails de la question d'audit</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/dashboard/admin/audits/questions/${questionId}/history`}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
          >
            <History className="h-4 w-4 mr-2" />
            Historique
          </Link>
          {getStatusBadge(question.status)}
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting ? 'Export en cours...' : 'Exporter PDF'}
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Question Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations de la question</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-green-600" />
                  Localisation
                </p>
                <p className="text-sm text-gray-900">{question.location || 'Non défini'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Exigences</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.requirements || 'Non défini'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Plan d'entretien</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.interview_plan || 'Non défini'}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Description de la question</p>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.question_description || 'Non défini'}</p>
              </div>

              {question.answer && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Réponse</p>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{question.answer}</p>
                </div>
              )}
            </div>
          </div>

          {/* Audit Section Information */}
          {question.audit_section && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-green-600" />
                Section d'audit
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Nom de la section</p>
                  <p className="text-sm text-gray-900 mt-1">{question.audit_section.section_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Localisation</p>
                  <p className="text-sm text-gray-900 mt-1">{question.audit_section.location || 'Non défini'}</p>
                </div>
                <div className="pt-2">
                  <Link
                    to={`/dashboard/admin/audits/sections/${question.audit_section.id}`}
                    className="text-sm text-green-600 hover:text-green-700 hover:underline"
                  >
                    Voir les détails de la section →
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Additional Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Informations supplémentaires</h3>
            <div className="space-y-3">
              {question.observation_effectiveness !== null && (
                <div>
                  <p className="text-xs text-gray-600">Efficacité de l'observation</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {question.observation_effectiveness ? 'Oui' : 'Non'}
                  </p>
                </div>
              )}
              {question.declared_gaps && (
                <div>
                  <p className="text-xs text-gray-600">Écarts déclarés</p>
                  <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{question.declared_gaps}</p>
                </div>
              )}
              {question.identified_best_practices && (
                <div>
                  <p className="text-xs text-gray-600">Meilleures pratiques identifiées</p>
                  <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{question.identified_best_practices}</p>
                </div>
              )}
              {question.interview_result && (
                <div>
                  <p className="text-xs text-gray-600">Résultat de l'entretien</p>
                  <p className="text-sm text-gray-900 mt-1">{question.interview_result}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-600">Confirmation de l'audité</p>
                <p className="text-sm text-gray-900 mt-1">{question.auditee_response_confirmation_display}</p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-green-600" />
              Métadonnées
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600">Créé par</p>
                <p className="text-sm text-gray-900 mt-1 flex items-center">
                  <User className="h-4 w-4 mr-1 text-gray-500" />
                  {question.created_by_name || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Date de création</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(question.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Dernière mise à jour</p>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(question.updated_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              {question.last_updated_by_name && (
                <div>
                  <p className="text-xs text-gray-600">Dernière modification par</p>
                  <p className="text-sm text-gray-900 mt-1 flex items-center">
                    <User className="h-4 w-4 mr-1 text-gray-500" />
                    {question.last_updated_by_name}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Question Risks */}
      {question.question_risks && question.question_risks.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2 text-red-600" />
            Risques associés ({question.question_risks.length})
          </h3>
          <div className="space-y-4">
            {question.question_risks.map((questionRisk) => {
              const probabilityConfig = {
                critical: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-200 text-red-900' },
                high: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', badge: 'bg-orange-200 text-orange-900' },
                medium: { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', badge: 'bg-yellow-200 text-yellow-900' },
                low: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-200 text-green-900' }
              };
              
              const config = probabilityConfig[questionRisk.probability] || probabilityConfig.medium;
              
              return (
                <div key={questionRisk.id} className={`border ${config.border} rounded-lg p-4 ${config.bg}`}>
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{questionRisk.risk_name}</h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                      {questionRisk.probability_display}
                    </span>
                  </div>
                  
                  {questionRisk.description && (
                    <p className="text-sm text-gray-700 mb-3">{questionRisk.description}</p>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Date du risque</p>
                      <p className="text-sm text-gray-900">
                        {new Date(questionRisk.risk_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 mb-1">Statut</p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                        {questionRisk.status}
                      </span>
                    </div>
                  </div>
                  
                  {questionRisk.consequence && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-600 mb-1">Conséquence/Impact</p>
                      <p className="text-sm text-gray-900">{questionRisk.consequence}</p>
                    </div>
                  )}
                  
                  {questionRisk.original_audit_plan_risk_id && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-600 mb-1">Risque original du plan d'audit</p>
                      <p className="text-sm text-gray-900">#{questionRisk.original_audit_plan_risk_id}</p>
                    </div>
                  )}
                  
                  {/* Risk Actions */}
                  {questionRisk.actions && questionRisk.actions.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-2">
                        Actions associées ({questionRisk.actions.length})
                      </p>
                      <div className="space-y-2">
                        {questionRisk.actions.map((action) => (
                          <div key={action.id} className="bg-white border border-gray-200 rounded p-3">
                            <p className="text-sm text-gray-900 mb-2">{action.action}</p>
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <div className="flex items-center space-x-4">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  action.is_completed 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {action.is_completed ? '✓ Complété' : '⏳ En attente'}
                                </span>
                                {action.due_date && (
                                  <span>Échéance: {new Date(action.due_date).toLocaleDateString('fr-FR')}</span>
                                )}
                              </div>
                              {action.user_assigned_name && (
                                <span>Assigné à: {action.user_assigned_name}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-3 pt-2 border-t border-gray-200 text-xs text-gray-500">
                    Créé le {new Date(questionRisk.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Initial Observations */}
      {question.initial_observations && question.initial_observations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Eye className="h-5 w-5 mr-2 text-blue-600" />
            Observations initiales ({question.initial_observations.length})
          </h3>
          
          <div className="space-y-3">
            {question.initial_observations.map((obs) => (
              <div key={obs.id} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{obs.observation}</p>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                  <span>Par: {obs.created_by_name}</span>
                  <span>{new Date(obs.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Observations */}
      {question.observations && question.observations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Eye className="h-5 w-5 mr-2 text-purple-600" />
            Observations ({question.observations.length})
          </h3>
          
          <div className="space-y-3">
            {question.observations.map((obs) => (
              <div key={obs.id} className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{obs.observation}</p>
                <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                  <span>Par: {obs.created_by_name}</span>
                  <span>{new Date(obs.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPIs */}
      {question.kpis && question.kpis.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-green-600" />
            KPIs ({question.kpis.length})
          </h3>
          
          <div className="space-y-3">
            {question.kpis.map((kpi) => (
              <div key={kpi.id} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900">{kpi.kpi_name}</h4>
                <div className="mt-2 grid grid-cols-2 gap-4">
                  {kpi.kpi_value && (
                    <div>
                      <p className="text-xs text-gray-600">Valeur</p>
                      <p className="text-sm text-gray-900">{kpi.kpi_value}</p>
                    </div>
                  )}
                  {kpi.kpi_target && (
                    <div>
                      <p className="text-xs text-gray-600">Cible</p>
                      <p className="text-sm text-gray-900">{kpi.kpi_target}</p>
                    </div>
                  )}
                </div>
                {kpi.kpi_description && (
                  <p className="text-sm text-gray-700 mt-2">{kpi.kpi_description}</p>
                )}
                <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                  <span>Par: {kpi.created_by_name}</span>
                  <span>{new Date(kpi.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachments */}
      {question.attachments && question.attachments.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-orange-600" />
            Pièces jointes ({question.attachments.length})
          </h3>
          
          <div className="space-y-3">
            {question.attachments.map((attachment) => (
              <div key={attachment.id} className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{attachment.file_name}</p>
                  <p className="text-xs text-gray-600 mt-1">
                    Taille: {formatFileSize(attachment.file_size)}
                  </p>
                  {attachment.description && (
                    <p className="text-sm text-gray-700 mt-1">{attachment.description}</p>
                  )}
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                    <span>Téléchargé par: {attachment.uploaded_by_name}</span>
                    <span>{new Date(attachment.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                  disabled={downloadingAttachment === attachment.id}
                  className="ml-4 inline-flex items-center px-3 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
                >
                  {downloadingAttachment === attachment.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Téléchargement...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-1" />
                      Télécharger
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations Count */}
      {question.recommendations && question.recommendations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="h-5 w-5 mr-2 text-indigo-600" />
            Recommandations ({question.recommendations.length})
          </h3>
          
          <div className="space-y-3">
            {question.recommendations.map((rec) => {
              const priorityConfig = {
                critical: { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-800', badge: 'bg-red-200 text-red-900' },
                high: { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-800', badge: 'bg-orange-200 text-orange-900' },
                medium: { bg: 'bg-yellow-100', border: 'border-yellow-300', text: 'text-yellow-800', badge: 'bg-yellow-200 text-yellow-900' },
                low: { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-800', badge: 'bg-green-200 text-green-900' }
              };
              
              const config = priorityConfig[rec.priority] || priorityConfig.medium;
              
              return (
                <div key={rec.id} className={`p-4 ${config.bg} border ${config.border} rounded-lg`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                      {rec.priority_display}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{rec.recommendation}</p>
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
                    <span>Par: {rec.created_by_name}</span>
                    <span>{new Date(rec.created_at).toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
